import { useT } from "@magic-md/i18n/react";
import {
    Activity,
    Cloud,
    Circle,
    HardDrive,
    Loader2,
    Package,
    Search,
    Server,
    X,
} from "lucide-react";
import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../lib/utils";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

export type SidecarSourceFilter = "all" | "local" | "cloud";

export interface SidecarListItemModel {
    id: string;
    title: string;
    icon?: string;
    description?: string;
    alive?: boolean;
    sourceKind?: "local" | "cloud" | string;
    transportType?: string;
    manifestSourceKind?: "local" | "cloud" | string;
    manifestTransportType?: string;
    readOnlyLabel?: string;
    loading?: boolean;
    disabled?: boolean;
    searchText?: string[];
    toggle?: {
        checked: boolean;
        disabled?: boolean;
        loading?: boolean;
        onChange: (checked: boolean) => void;
    };
}

export const SIDECAR_SOURCE_FILTERS: SidecarSourceFilter[] = [
    "all",
    "local",
    "cloud",
];

export function isCloudSidecarListItem(
    item: Pick<
        SidecarListItemModel,
        "sourceKind" | "transportType" | "manifestSourceKind" | "manifestTransportType"
    >,
): boolean {
    return (
        item.sourceKind === "cloud" ||
        item.transportType === "cloud-worker-http" ||
        item.manifestSourceKind === "cloud" ||
        item.manifestTransportType === "cloud-worker-http"
    );
}

export function filterSidecarListItemsBySource<T extends SidecarListItemModel>(
    items: T[],
    sourceFilter: SidecarSourceFilter,
): T[] {
    if (sourceFilter === "all") return items;
    return items.filter((item) => {
        const isCloud = isCloudSidecarListItem(item);
        return sourceFilter === "cloud" ? isCloud : !isCloud;
    });
}

export function countSidecarSources(
    items: SidecarListItemModel[],
): Record<SidecarSourceFilter, number> {
    let local = 0;
    let cloud = 0;
    for (const item of items) {
        if (isCloudSidecarListItem(item)) cloud += 1;
        else local += 1;
    }
    return {
        all: items.length,
        local,
        cloud,
    };
}

export function sidecarSourceFilterLabel(
    t: ReturnType<typeof useT>,
    filter: SidecarSourceFilter,
): string {
    if (filter === "cloud") return t("sidecarPanel.filter.cloud");
    if (filter === "local") return t("sidecarPanel.filter.local");
    return t("sidecarPanel.filter.all");
}

export function SidecarList({
    items,
    selectedId,
    onSelect,
    title,
    searchQuery,
    onSearchChange,
    sourceFilter,
    onSourceFilterChange,
    searchPlaceholder,
    emptyMessage,
    noMatchesMessage,
    showSearch = true,
    showSourceFilter = true,
    toolbarAction,
    rightAction,
    className,
}: {
    items: SidecarListItemModel[];
    selectedId?: string | null;
    onSelect?: (id: string) => void;
    title?: string;
    searchQuery?: string;
    onSearchChange?: (query: string) => void;
    sourceFilter?: SidecarSourceFilter;
    onSourceFilterChange?: (filter: SidecarSourceFilter) => void;
    searchPlaceholder?: string;
    emptyMessage?: string;
    noMatchesMessage?: string;
    showSearch?: boolean;
    showSourceFilter?: boolean;
    toolbarAction?: ReactNode;
    rightAction?: (item: SidecarListItemModel) => ReactNode;
    className?: string;
}) {
    const t = useT("editor");
    const [internalSearch, setInternalSearch] = useState("");
    const [internalSourceFilter, setInternalSourceFilter] =
        useState<SidecarSourceFilter>("all");
    const activeSearch = searchQuery ?? internalSearch;
    const activeSourceFilter = sourceFilter ?? internalSourceFilter;
    const setSearch = onSearchChange ?? setInternalSearch;
    const setSourceFilter = onSourceFilterChange ?? setInternalSourceFilter;
    const counts = useMemo(() => countSidecarSources(items), [items]);
    const filteredItems = useMemo(() => {
        const q = activeSearch.trim().toLowerCase();
        return filterSidecarListItemsBySource(items, activeSourceFilter).filter((item) => {
            if (!q) return true;
            return buildSidecarSearchTerms(item)
                .some((value) => value.toLowerCase().includes(q));
        });
    }, [activeSearch, activeSourceFilter, items]);
    const hasFilters = activeSearch.trim().length > 0 || activeSourceFilter !== "all";

    return (
        <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
            {(showSearch || showSourceFilter || title || toolbarAction) ? (
                <div className="border-b border-border px-2 py-2">
                    {title ? (
                        <div className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {title}
                        </div>
                    ) : null}
                    {(showSearch || showSourceFilter || toolbarAction) ? (
                        <div className="flex items-center gap-1.5">
                            {showSearch ? (
                                <div className="relative min-w-0 flex-1">
                                    <Search
                                        size={12}
                                        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/50"
                                    />
                                    <input
                                        type="text"
                                        className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-7 text-xs placeholder:text-muted-foreground/40 outline-none transition-colors focus:border-primary"
                                        placeholder={searchPlaceholder ?? t("sidecarPanel.searchPlaceholder")}
                                        value={activeSearch}
                                        onChange={(event) => setSearch(event.target.value)}
                                    />
                                    {activeSearch ? (
                                        <button
                                            type="button"
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground/40 transition-colors hover:text-foreground"
                                            onClick={() => setSearch("")}
                                        >
                                            <X size={12} />
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                            {toolbarAction ? (
                                <div className="shrink-0">{toolbarAction}</div>
                            ) : null}
                            {showSourceFilter ? (
                                <SidecarSourceTabs
                                    value={activeSourceFilter}
                                    counts={counts}
                                    onChange={setSourceFilter}
                                    className="mt-0 shrink-0"
                                />
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-auto">
                {filteredItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
                        <Server size={16} className="text-muted-foreground/40" />
                        <p className="text-[10px] text-muted-foreground/50">
                            {hasFilters
                                ? (noMatchesMessage ?? t("sidecarPanel.noMatching"))
                                : (emptyMessage ?? t("sidecarPanel.empty"))}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-border/30">
                        {filteredItems.map((item) => (
                            <SidecarListItem
                                key={item.id}
                                item={item}
                                selected={selectedId === item.id}
                                onSelect={onSelect}
                                rightAction={rightAction}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export function SidecarSourceTabs({
    value,
    counts,
    onChange,
    className,
}: {
    value: SidecarSourceFilter;
    counts: Record<SidecarSourceFilter, number>;
    onChange: (filter: SidecarSourceFilter) => void;
    className?: string;
}) {
    const t = useT("editor");
    return (
        <div className={cn("mt-2 inline-flex items-center rounded-md border border-border/70 bg-muted/20 p-0.5", className)}>
            {SIDECAR_SOURCE_FILTERS.map((filter) => {
                const active = value === filter;
                return (
                    <button
                        key={filter}
                        type="button"
                        onClick={() => onChange(filter)}
                        className={cn(
                            "inline-flex h-6 min-w-8 shrink-0 items-center justify-center gap-1 rounded px-1.5 text-[10px] transition-colors",
                            active
                                ? "bg-background text-primary shadow-sm"
                                : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
                        )}
                        title={sidecarSourceFilterLabel(t, filter)}
                        aria-label={sidecarSourceFilterLabel(t, filter)}
                    >
                        <SidecarFilterIcon filter={filter} />
                        <span className="tabular-nums opacity-70">{counts[filter]}</span>
                    </button>
                );
            })}
        </div>
    );
}

function SidecarFilterIcon({ filter }: { filter: SidecarSourceFilter }) {
    if (filter === "cloud") return <Cloud size={12} />;
    if (filter === "local") return <HardDrive size={12} />;
    return <Package size={12} />;
}

function SidecarListItem({
    item,
    selected,
    onSelect,
    rightAction,
}: {
    item: SidecarListItemModel;
    selected: boolean;
    onSelect?: (id: string) => void;
    rightAction?: (item: SidecarListItemModel) => ReactNode;
}) {
    const t = useT("editor");
    const interactive = !!onSelect && !item.disabled;
    const isCloud = isCloudSidecarListItem(item);
    const action = rightAction?.(item);
    const handleSelect = () => {
        if (interactive) onSelect(item.id);
    };
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!interactive) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(item.id);
        }
    };

    return (
        <div
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
            className={cn(
                "group flex w-full items-start gap-2 px-3 py-1.5 text-left transition-colors outline-none",
                selected ? "bg-primary/5" : interactive ? "hover:bg-muted/40" : "cursor-default",
                interactive && "focus-visible:bg-muted/40 focus-visible:ring-1 focus-visible:ring-primary/30",
                item.disabled && "opacity-75",
            )}
        >
            {item.toggle ? (
                <label
                    className="mt-0.5 flex items-center"
                    onClick={(event) => event.stopPropagation()}
                >
                    <input
                        type="checkbox"
                        checked={item.toggle.checked}
                        disabled={item.toggle.disabled || item.toggle.loading}
                        onChange={(event) => item.toggle?.onChange(event.target.checked)}
                    />
                </label>
            ) : null}
            <span className="mt-0.5 shrink-0 text-sm leading-none">
                {item.icon ?? "⚙"}
            </span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        <span className="truncate text-xs font-medium text-foreground/80">
                            {item.title}
                        </span>
                        <SidecarStatusIcon item={item} />
                        {item.readOnlyLabel ? (
                            <span className="shrink-0 rounded border border-border/60 bg-muted/40 px-1 py-0.5 text-[10px] text-muted-foreground">
                                {item.readOnlyLabel}
                            </span>
                        ) : null}
                        {item.loading || item.toggle?.loading ? (
                            <Loader2 size={12} className="shrink-0 animate-spin text-muted-foreground" />
                        ) : null}
                    </div>
                    {isCloud ? (
                        <span
                            title={t("sidecarPanel.cloudSource")}
                            className="ml-auto inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/5 text-primary"
                        >
                            <Cloud size={11} />
                        </span>
                    ) : null}
                    {action ? (
                        <span
                            className="shrink-0"
                            onClick={(event) => event.stopPropagation()}
                        >
                            {action}
                        </span>
                    ) : null}
                </div>
                {item.description ? (
                    <div
                        className="mt-1 truncate text-xs text-muted-foreground"
                        title={item.description}
                    >
                        {item.description}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function SidecarStatusIcon({ item }: { item: SidecarListItemModel }) {
    if (item.alive === true) {
        return (
            <Activity
                size={10}
                className="shrink-0 theme-accent-text"
                style={getThemeAccentStyle("success")}
            />
        );
    }
    if (item.alive === false) {
        return (
            <Circle
                size={8}
                className="shrink-0 text-muted-foreground/30"
            />
        );
    }
    return null;
}

function buildSidecarSearchTerms(item: SidecarListItemModel): string[] {
    return [
        item.id,
        item.title,
        item.description,
        item.sourceKind,
        item.transportType,
        item.manifestSourceKind,
        item.manifestTransportType,
        ...(item.searchText ?? []),
    ].filter((value): value is string => !!value);
}
